import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { KOGTableRow } from "@/main/components/shared/kogBoxGrid/styles";
import { TextEllipsis } from "@/main/components/shared/styles";
import { getDefaultMessageResultsFound, getTranslateByKey } from "@/main/services/utils";
import { IconButton, TableCell } from "@mui/material";
import * as S from "./styles";
import * as StylesShared from "@/main/components/shared/styles";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { useEffect, useState } from "react";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import Search from "@mui/icons-material/Search";
import { CompanyHistoricalResponse } from "@/main/models/response/companyHistoricalResponse";
import CompanyHistoricalSidebarDetails from "./companyHistoricalSidebarDetails";
import { isNil } from "lodash";
import { CompanyHistoricalRequest } from "@/main/models/request/companyHistoricalRequest";
import { companyHistoricalApi } from "@/main/api";

export interface IDataGridCompanyTypeHistorical {
	data: CompanyHistoricalResponse[];
	totalItems: number;
	isLoading: boolean;
}

interface Props {
	gridData: IDataGridCompanyTypeHistorical;
	setGridData: React.Dispatch<React.SetStateAction<IDataGridCompanyTypeHistorical>>;
	formFilters: CompanyHistoricalRequest | null;
}

export default function CompanyHistoricalGrid({ gridData, formFilters, setGridData }: Props) {
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [openDetails, setOpenDetails] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [historicalItemSelected, setHistoricalItemSelected] = useState<CompanyHistoricalResponse | null>(null);

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data, gridData.totalItems]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [formFilters]);

	function onLoadInfiniteScroll(request: CompanyHistoricalRequest) {
		setIsLoadingInfiniteScroll(true);

		const requestGrid: CompanyHistoricalRequest = {
			...request,
			pageIndex: request.pageIndex ?? PaginationEnum.PageIndexIntial,
			pageSize: request.pageSize ?? PaginationEnum.PageSize30,
		};

		companyHistoricalApi
			.getHistorical(requestGrid)
			.then((response) => {
				let resultData: CompanyHistoricalResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}

				const newData = resultData.filter(
					(item: any) => !gridData.data.some((existing: any) => existing.guid === item.guid),
				);
				const updatedData = [...gridData.data, ...newData];
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: updatedData,
				});
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid() {
		setPageIndex((prev) => prev + 1);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: pageIndex + 1,
			};
			onLoadInfiniteScroll(updatedFormFilters);
		}
	}

	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);

	const rowHeaders = [
		getTranslateByKey("information.action"),
		getTranslateByKey("information.dateHourUser"),
		<StylesShared.RowHeaderTextCenter key="2">
			{getTranslateByKey("information.actions")}
		</StylesShared.RowHeaderTextCenter>,
	];

	function handleCloseDetails() {
		setOpenDetails(false);
		setHistoricalItemSelected(null);
	}

	function handleOpenDetails(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: CompanyHistoricalResponse) {
		event.preventDefault();
		setHistoricalItemSelected(item);
		setOpenDetails(true);
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters === null) return null;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<>
			<S.Container>
				<BoxGrid
					title={titleGrid}
					sizeColumns={sizeColumns}
					rowHeaders={rowHeaders}
					marginCustom="0px"
					infiniteScrollConfig={{
						dataLength: gridData.data.length,
						fetchData: () => onLoadSearchGrid(),
						hasMore: hasShowMore,
						isLoading: isLoadingInfiniteScroll,
					}}
				>
					{gridData.data.map((item: CompanyHistoricalResponse) => (
						<KOGTableRow key={item.guid} id={`id-searchCompanyHistorical-tableRow-result-${item.guid}`}>
							<TableCell>
								<TextEllipsis title={item.actionName}>{item.actionName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={item.dateHourUser}>{item.dateHourUser}</TextEllipsis>
							</TableCell>

							<TableCell align="center">
								<IconButton
									id={`id-searchCompanyHistorical-btn-details-${item.guid}`}
									size="small"
									color="primary"
									onClick={(event) => handleOpenDetails(event, item)}
								>
									<Search fontSize="medium" />
								</IconButton>
							</TableCell>
						</KOGTableRow>
					))}
				</BoxGrid>
			</S.Container>

			{!isNil(historicalItemSelected) && openDetails && (
				<CompanyHistoricalSidebarDetails
					open={openDetails}
					itemSelected={historicalItemSelected}
					closeModal={handleCloseDetails}
				/>
			)}
		</>
	);
}

const sizeColumns = ["45%", "45%", "10%"];
